<template>
  <application-layout data-testid="dashboard-view">
    <v-container fluid class="mx-auto px-6">
      <v-row>
        <v-col cols="5">
          <v-card outlined class="outlined pa-4 mb-6 elevation-0 rounded-lg">
            <header
              class="text-h5 mb-2 elevation-0 rounded-0 v-sheet py-1"
              style="background: transparent"
            >
              Initiate Outbound Call
            </header>
            <v-row class="mb-2">
              <v-col cols="6">
                <app-input
                  data-testid="outbound-call-location-code-input"
                  class="outbound-call-location-code-input"
                  v-model="locationCode"
                  :type="'text'"
                  label="Location Code"
                  placeholder="Enter Location Code..."
                  :rules="{ required: true }"
                  @keyup="$e => handleKeyup($e, 'selectOutboundCall')"
                >
                  Location Code
                </app-input>
              </v-col>
              <v-col cols="6">
                <app-input
                  data-testid="outbound-call-call-id-input"
                  class="outbound-call-call-id-input"
                  v-model="callId"
                  :type="'number'"
                  label="Call ID"
                  placeholder="Enter Call ID..."
                  :rules="{ required: true }"
                  ref="outboundCallInput"
                  @keyup="$e => handleKeyup($e, 'submit')"
                >
                  Call ID
                </app-input>
              </v-col>
            </v-row>
            <v-btn
              data-testid="outbound-begin-call-btn"
              class="outbound-begin-call-btn my-0"
              color="primary white--text"
              @click="handleStandardCall"
              block
              :disabled="!validCallId || phoneActive"
              :loading="callLoading"
            >
              Begin Call
            </v-btn>
          </v-card>
          <leads-card class="mt-2 mb-6" />
          <follow-up-card class="mt-2 mb-6" />
        </v-col>
        <v-col cols="7">
          <v-card outlined class="outlined mb-6 elevation-0 rounded-lg">
            <v-layout class="pa-4">
              <header
                class="text-h5 mb-2 elevation-0 rounded-0 v-sheet py-1"
                style="background: transparent"
              >
                Deals & Phone Rentals
              </header>
            </v-layout>
            <current-agent-overview />
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <application-modal v-model="incomingCall">
      <div slot="modal-title">Incoming Call</div>
      <div slot="modal-body" class="subheading">
        Incoming call for Facility {{ locationCode }}.
      </div>
      <span slot="cancel-text">Reject Call</span>
      <v-btn
        data-testid="sales-accept-call-btn"
        class="sales-accept-call-btn primary"
        slot="create-button"
        @click="handleStandardCall"
      >
        Take Call
      </v-btn>
    </application-modal>

    <application-modal v-model="incomingVideoCall">
      <div slot="modal-title">
        Incoming Video Call for Facility {{ locationCode }}
      </div>
      <div slot="modal-body" class="subheading">
        Incoming video call from
        <span class="info--text font-weight-bold">{{ videoCustomer }}</span
        >.
      </div>
      <span slot="cancel-text">Reject Call</span>
      <v-btn
        data-testid="sales-accept-video-call-btn"
        class="sales-accept-video-call-btn primary"
        slot="create-button"
        @click="handleVideoCall"
        :loading="vidchatLoading"
      >
        Take Call
      </v-btn>
    </application-modal>
  </application-layout>
</template>

<script>
import ApplicationLayout from "@/layouts/ApplicationLayout";
import ApplicationModal from "@/components/ApplicationModal";
import CurrentAgentOverview from "./partials/CurrentAgentOverview";
import FollowUpCard from "@/views/dashboard/partials/FollowUpCard";
import LeadsCard from "@/views/dashboard/partials/LeadsCard";
import Pusher from "pusher-js";
import { inputRules } from "@/helpers/form-rules";

export default {
  data() {
    return {
      callLoading: false,
      vidchatLoading: false,
      incomingCall: false,
      incomingVideoCall: false,

      location: "dashboard",
      filterByLocation: "",
      locationCode: "",
      callId: "",
      deviceId: "",
      videoCustomer: "",
      roomName: "",
      inputRules: inputRules,

      pusher: null,
      callsUpdate: null
    };
  },

  methods: {
    handleKeyup(event, type) {
      if (event.which === 13) { // Enter key
        if (type === "submit" && !this.phoneActive && this.validCallId) {
          this.handleStandardCall();
        }
        if (type === "selectOutboundCall") {
          this.$refs.outboundCallInput.$refs.appInput.focus();
        }
      }
    },
    handleStandardCall() {
      let payload = {
        call_type: "incoming",
        ctm_id: parseFloat(this.callId),
        location_code: this.locationCode.toUpperCase(),
        waiting_list_id: null
      };

      this.callLoading = true;
      this.$store
        .dispatch("beginCall", payload)
        .then((resp) => {
          let localStoragePayload = {
            locationCode: payload.location_code,
            reservationId: null,
            callId: resp.data.call.id,
            ctm_id: payload.ctm_id,
            call_type: payload.call_type,
            router: {
              name: "facilityDetails",
              facilityId: resp.facilityId
            },
            contact_id: null,
            tenant_id: null
          };

          this.$store.dispatch("routeCall", localStoragePayload);
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
          this.callLoading = false;
        });
    },

    handleVideoCall() {
      this.vidchatLoading = true;
      const payload = {
        location_code: this.locationCode,
        room_name: this.roomName,
        device_id: this.deviceId
      };
      this.$store
        .dispatch("answerVideoCall", payload)
        .then(() => {
          localStorage.setItem("locationCode", this.locationCode);
          this.$store.commit("activateTwilioChat");
          this.$store.commit("setVideoCall");
          this.$store.commit("setVideoRoomName", this.roomName);
          this.$store.commit("setVideoCustomer", this.videoCustomer);
          this.vidchatLoading = false;
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
          this.vidchatLoading = false;
        });
    },

    subscribeToIncomingCalls() {
      if (!this.pusher) {
        this.pusher = new Pusher(process.env.VUE_APP_PUSHER_KEY, {
          cluster: "us2",
          forceTLS: true,
          authEndpoint: process.env.VUE_APP_API_HOST + "/auth/pusher",
          auth: {
            headers: {
              Authorization: sessionStorage.getItem("access_token")
            }
          }
        });
      }
      // CTM Call Events
      const channel = this.pusher.subscribe("private-incoming-call");
      channel.bind("App\\Events\\IncomingCall", (data) => {
        this.handleIncomingCall(data.payload);
      });
      // Video Call Events
      const videoChannel = this.pusher.subscribe("private-video-call");
      videoChannel.bind("App\\Events\\IncomingVideoCall", (data) => {
        this.handleIncomingVideoCall(data.payload);
      });
      videoChannel.bind("App\\Events\\VideoCallAnswered", (data) => {
        this.videoCallAlreadyAnswered(data.payload);
      });
      videoChannel.bind("App\\Events\\VideoCallCancelled", () => {
        this.videoCallCancelled();
      });
    },

    handleIncomingCall(payload) {
      if (payload.ctm_user_id === this.$store.state.user.me.ctm_id) {
        this.callId = payload.ctm_id;
        this.locationCode = payload.location_code;
        this.incomingCall = true;
      }
    },

    handleIncomingVideoCall(payload) {
      this.deviceId = payload.device_id;
      this.roomName = payload.room_name;
      this.locationCode =
        payload.location_code === "Kiosk" ? "LTRAIN" : payload.location_code;
      this.videoCustomer = payload.name;
      this.incomingVideoCall = true;
    },

    videoCallAlreadyAnswered(payload) {
      if (payload.user.email !== this.me.email) this.incomingVideoCall = false;
    },

    videoCallCancelled() {
      this.roomName = "";
      this.locationCode = "";
      this.name = "";
      this.incomingVideoCall = false;
    },

    unsubscribeToIncomingCalls() {
      this.pusher.disconnect();
      this.pusher = null;
    }
  },

  computed: {
    validCallId() {
      return !!(this.locationCode && this.callId);
    },

    phoneActive() {
      return !!localStorage.getItem("callStarted");
    },

    me() {
      return this.$store.state.user.me;
    }
  },

  created() {
    if (sessionStorage.getItem("access_token")) {
      this.subscribeToIncomingCalls();
    }
    this.$store.commit("resetTenant");
    this.$store.dispatch("getAllGlobalSettings");
  },

  beforeDestroy() {
    clearInterval(this.subscribeToIncomingCalls);
    this.unsubscribeToIncomingCalls();
  },

  components: {
    FollowUpCard,
    CurrentAgentOverview,
    ApplicationLayout,
    ApplicationModal,
    LeadsCard
  }
};
</script>
